import {
  Button,
  Calendar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  showErrorToast,
} from '@bpinternal/ui-kit-next'
import { Check } from 'lucide-react'
import { ChartDateShortcut, chartDateShortcuts, dateShortcutDetails, getRangeFromShortcut } from '~/features/analytics'
import { useState, type FC } from 'react'
import { DateRange } from 'react-day-picker'
import { cn } from '~/utils'

type Props = {
  children?: React.ReactNode
  className?: string
  dateRange?: DateRange
  dateShortcut?: ChartDateShortcut
  onRangeSelect?: (range: DateRange | undefined) => void
  onShortcutSelect?: (shortcut: ChartDateShortcut) => void
}

export const DatePickerAnalyticsPopover: FC<Props> = ({
  children,
  className,
  dateRange,
  dateShortcut,
  onRangeSelect,
  onShortcutSelect,
}) => {
  const [open, setOpen] = useState(false)
  const [range, setRange] = useState<DateRange | undefined>({ from: dateRange?.from, to: dateRange?.to })
  const [lastSelectedDay, setLastSelectedDay] = useState<Date | undefined>(dateRange?.to)
  const [selectedShortcut, setSelectedShortcut] = useState<ChartDateShortcut | undefined>(dateShortcut)

  const daysInRange = (range: DateRange | undefined) => {
    if (!range?.from || !range?.to) return 0

    const start = new Date(range.from)
    const end = new Date(range.to)

    start.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)
    const msPerDay = 1000 * 60 * 60 * 24
    const diff = (end.getTime() - start.getTime()) / msPerDay

    return Math.abs(diff) + 1
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent className={cn('no-drag flex gap-1 p-3', className)}>
        <div className="flex flex-col pt-8">
          {chartDateShortcuts.map((shortcut) => (
            <Button
              className="justify-start"
              variant="ghost"
              key={shortcut}
              onClick={() => {
                setRange(getRangeFromShortcut(shortcut))
                onShortcutSelect?.(shortcut)
                setOpen(false)
                setLastSelectedDay(new Date())
                setSelectedShortcut(shortcut)
              }}
            >
              {dateShortcutDetails[shortcut].label}
              {shortcut === selectedShortcut ? <Check /> : <></>}
            </Button>
          ))}
        </div>
        <Calendar
          className="p-0"
          modifiersClassNames={{
            today: 'border border-gray-9',
            selected: 'hover:bg-blue-9 bg-blue-9 hover:text-gray-1 text-gray-1',
          }}
          mode="range"
          selected={range}
          defaultMonth={lastSelectedDay}
          onDayClick={(day) => setLastSelectedDay(day)}
          onSelect={(range) => {
            const isRangeValid = daysInRange(range) <= 365
            const fromTime = range?.from?.setHours(0, 0, 0, 0)
            const toTime = range?.to?.setHours(0, 0, 0, 0)
            if (!isRangeValid) {
              showErrorToast('Date range can not be greater than 365 days')
              return
            }
            if (fromTime !== toTime) {
              setRange(range)
              onRangeSelect?.(range)
            } else {
              setRange(undefined)
              onRangeSelect?.(undefined)
            }
            setSelectedShortcut(undefined)
          }}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
